import {
  // reason: this is the wrapped component which we will allow since this is the exported version
  // eslint-disable-next-line @dhl/dhl-ui/no-restricted-imports
  Button as MuiButton,
  type ButtonProps as MuiButtonProps,
} from '@mui/material'
import { type SystemCssProperties } from '@dhl-ui/theme'
import { LoadingIndicator } from '../LoadingIndicator'
import type { BaseProps, MinimalSxPadding } from '../../types'
import type { BaseButtonComponentProps } from './types'

export type BaseButtonProps = BaseProps<HTMLButtonElement, MinimalSxPadding, Pick<SystemCssProperties, 'textWrap'>> &
  Pick<
    MuiButtonProps,
    | 'autoFocus'
    | 'children'
    | 'color'
    | 'component'
    | 'disabled'
    | 'endIcon'
    | 'form'
    | 'fullWidth'
    | 'id'
    | 'loading'
    | 'onClick'
    | 'onKeyDown'
    | 'onMouseDown'
    | 'startIcon'
    | 'type'
    | 'value'
    | 'variant'
    | 'ref'
  > &
  BaseButtonComponentProps & {
    /**
     * @default 'center'
     */
    align?: 'start' | 'center'

    /**
     * @default undefined, only applicable for variant="text"
     */
    dense?: boolean

    /**
     * Have a square button as option, biggest use case is on the variant `text`
     */
    isSquare?: boolean

    /**
     * Size of the button
     * @default 'medium'
     */
    size?: 'small' | 'medium' | 'large' | 'xs'
    /**
     * non emphasize the label
     * @default true
     */
    noEmphasize?: boolean
  }

/**
 * only textVariant has both startIcon and endIcon
 * Contained and Outlined only know startIcon
 *
 * This BaseButton is for INTERNAL usage only, this to allow certain attributes (e.g. `color="inherit"`)
 * whereas these attributes are NOT meant for assignment in external implementation (for that, we have `Button`)
 */
export const BaseButton = ({
  children,
  isSquare,
  sx,
  align = 'center',
  dense = false,
  size = 'medium',
  noEmphasize = false,
  ...props
}: BaseButtonProps) => (
  <MuiButton
    {...props}
    size={size}
    align={align}
    loadingPosition="start"
    sx={sx}
    className={`${dense ? 'DhlButton-dense' : ''}${isSquare ? ' DhlButton-square' : ''}${noEmphasize ? ' DhlButton-notEmphasized' : ''}`}
    loadingIndicator={<LoadingIndicator color="inherit" />}
  >
    {children}
  </MuiButton>
)
